import { Button, type ButtonProps } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { LINK_HIT_SLOP } from './hit-slop';

/**
 * The auth flow's text links.
 *
 * These were nine hand-rolled `Pressable` + `Text` pairs, none of which had press
 * feedback, a role, or a disabled state -- so every link was dead on press and
 * announced as plain text while every button responded. Routing them through the
 * design-system `Button` buys all three back from the system.
 *
 * Two deliberate departures from `Button`'s own `link` variant:
 * - **Colour.** The `link` variant is `text-primary`. Green marks action, selection,
 *   focus and success (DESIGN.md's One Green Rule); a green link next to the green
 *   CTA would put three greens on the login screen. Warm-gray underline instead.
 * - **Box.** `Button`'s size classes make a `h-12 px-4 py-3` control. An inline link
 *   inside a sentence must shrink-wrap, so the box is stripped back and the touch
 *   target is bought with `hitSlop` (see `hit-slop.ts`).
 *
 * `Button` renders non-string children verbatim, so passing a `Text` child is how it
 * keeps our type treatment while still supplying role, `active:opacity-80`, and the
 * `opacity-50` disabled dim.
 */
export interface AuthLinkProps extends Omit<ButtonProps, 'variant' | 'size' | 'children'> {
  children: string;
  /** `strong` is the primary link in a footer pair ("Don't have an account? **Sign up**"). */
  tone?: 'strong' | 'muted';
}

export function AuthLink({ children, tone = 'strong', className, ...props }: AuthLinkProps) {
  return (
    <Button
      variant="ghost"
      className={cn('h-auto p-0', className)}
      hitSlop={LINK_HIT_SLOP}
      {...props}
    >
      <Text
        className={cn(
          'text-sm underline',
          tone === 'strong' ? 'font-semibold text-warm-gray-100' : 'text-warm-gray-300'
        )}
      >
        {children}
      </Text>
    </Button>
  );
}
